/**
 * sl-kypm.5.1: Marketplace API Hook (SWR-native)
 *
 * Fetches 3-level marketplace templates from the backend REST API.
 * Supports app, app block, doc, step, workflow, and automation template types.
 * Caches results for 5 minutes. No external dependencies (SWR/React-Query).
 *
 * SOLID:
 * - S: Only data fetching + caching for marketplace templates
 */
import type { CustomAppBlockCapabilityClass } from '@streamlain/view-config';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { workspaceApiFetch } from '../../../modules/cloud/services/workspace-api-client';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '../../../utils/backend-availability';
import { useMarketplaceInstallStateRefresh } from './marketplace-install-state';

// =============================================================================
// Types
// =============================================================================

export interface MarketplaceTemplate {
  id: string;
  templateType:
    | 'doc'
    | 'app'
    | 'app_block'
    | 'step'
    | 'workflow'
    | 'automation';
  /**
   * sl-k94y.1.1: Multi-language title map keyed by BCP-47 tag (en baseline
   * enforced at every backend write boundary). Replaces the former scalar
   * `title: string` — render sites read via `pickTitle` from `./locale-utils`.
   */
  titlesI18n: Record<string, string>;
  /**
   * sl-eglf.1.2: Multi-language description map keyed by BCP-47 tag. The
   * legacy single-language `description` field is gone; render sites read
   * a locale via `pickDescription` from `./locale-utils`.
   */
  descriptionsI18n: Record<string, string>;
  category: string;
  tags: string[];
  publishedByUserId: string;
  publishedByWorkspaceId: string | null;
  publishedByName?: string;
  visibility: 'GLOBAL' | 'WORKSPACE' | 'TENANT' | 'SYSTEM';
  status: string;
  downloadCount: number;
  /** sl-u319.4.2: Execution count from marketplace analytics */
  executionCount?: number;
  averageRating: number;
  credentialRequirements: CredentialRequirement[];
  previewImageBlobId?: string;
  /** Active immutable card/detail artifacts; parsed by the shared media VM. */
  previewSet?: unknown;
  versions?: TemplateVersion[];
  installedVersionNumber?: number;
  createdAt: string;
  updatedAt: string;
  // sl-kypm: Step-specific fields
  stepIconBlobId?: string;
  stepInsertType?: string;
  relatedWorkflowTemplateId?: string | null;
  appPresetKey?: string | null;
  appBlockIds?: string[];
  /** Minimum runtime capability required by an App Block. */
  appBlockCapabilityClass?: CustomAppBlockCapabilityClass | null;
  templateFacets?: Array<
    'app' | 'app_block' | 'node' | 'agent' | 'workflow' | 'automation'
  >;
  publisher?: string;
  verified?: boolean;
  provenance?: 'streamlain_built' | 'streamlain_certified' | 'community_built';
  isPreinstalled?: boolean;
  latestVersion?: number;
  agentDefinition?: {
    schemaVersion: 1;
    kind: 'agent';
    identity: {
      name: string;
      descriptionsI18n: Record<string, string>;
      badge: 'agent';
    };
  } | null;
}

export interface CredentialRequirement {
  type: string;
  label: string;
  required: boolean;
  isProvidedByStreamlain: boolean;
  description?: string;
}

export interface TemplateVersion {
  versionNumber: number;
  semanticVersion: string;
  changelogI18n?: Record<string, string>;
  changelog: string;
  createdAt: string;
}

export interface MarketplaceCategory {
  name: string;
  count: number;
}

export interface BrowseFilter {
  search?: string;
  category?: string;
  featured?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'type' | 'downloads' | 'rating' | 'newest';
  templateType?:
    | 'doc'
    | 'app'
    | 'app_block'
    | 'step'
    | 'workflow'
    | 'automation';
  templateFacet?:
    | 'app'
    | 'app_block'
    | 'node'
    | 'agent'
    | 'workflow'
    | 'automation';
}

export type MarketplaceBrowseTypeTab =
  | 'all'
  | 'app'
  | 'app_block'
  | 'agent'
  | 'doc'
  | 'step'
  | 'workflow'
  | 'automation';

export type MarketplaceBrowseTypeCounts = Partial<
  Record<MarketplaceBrowseTypeTab, number>
>;

export const MARKETPLACE_BROWSE_COUNTED_TYPE_TABS = [
  'app',
  'app_block',
  'agent',
  'doc',
  'automation',
  'workflow',
  'step',
] as const satisfies ReadonlyArray<Exclude<MarketplaceBrowseTypeTab, 'all'>>;

export function deriveBrowseTypeFilter(
  tab: MarketplaceBrowseTypeTab
): Pick<BrowseFilter, 'templateType' | 'templateFacet'> {
  return {
    templateType:
      tab === 'doc' ||
      tab === 'app' ||
      tab === 'app_block' ||
      tab === 'step' ||
      tab === 'workflow' ||
      tab === 'automation'
        ? tab
        : undefined,
    templateFacet: tab === 'agent' ? 'agent' : undefined,
  };
}

export function isMarketplaceBrowseTypeAvailable(
  tab: MarketplaceBrowseTypeTab,
  counts: MarketplaceBrowseTypeCounts | null,
  options: {
    readonly expertAgentsAllowed: boolean;
    readonly appsAllowed?: boolean;
  }
): boolean {
  if (tab === 'all') return true;
  if (tab === 'agent' && !options.expertAgentsAllowed) return false;
  if ((tab === 'app' || tab === 'app_block') && options.appsAllowed === false) {
    return false;
  }
  if (!counts) return true;
  const count = counts[tab];
  return count == null || count > 0;
}

export interface BrowseResult {
  items: MarketplaceTemplate[];
  total: number;
  page: number;
  limit: number;
}

export function mergeMarketplaceBrowseResults(
  publicResult: BrowseResult,
  workspaceResult: BrowseResult
): BrowseResult {
  const itemsById = new Map(
    [...publicResult.items, ...workspaceResult.items].map(item => [
      item.id,
      item,
    ])
  );
  return {
    items: [...itemsById.values()],
    total: itemsById.size,
    page: 1,
    limit: publicResult.limit + workspaceResult.limit,
  };
}

// =============================================================================
// Cache
// =============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * Drop cached catalogue projections after a publication was hidden, restored,
 * or deleted. Workspace publications can affect template lists, type counts,
 * and categories, including the combined "available" catalogue.
 */
export function invalidateMarketplaceCatalogCache(
  workspaceId: string | null
): void {
  if (!workspaceId) {
    cache.clear();
    return;
  }

  const scopedKeys = [
    `templates:${workspaceId}:`,
    `templates:available:${workspaceId}:`,
    `marketplace:type-counts:${workspaceId}`,
    `marketplace:categories:${workspaceId}`,
  ];
  for (const key of cache.keys()) {
    if (scopedKeys.some(prefix => key.startsWith(prefix))) {
      cache.delete(key);
    }
  }
}

async function marketplaceFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  workspaceId?: string | null
): Promise<Response> {
  if (workspaceId || isWorkspaceScopedMarketplaceRequest(input)) {
    return workspaceApiFetch(input, init, {
      ...(workspaceId ? { workspaceId } : {}),
      category: 'workspace-api',
      mode: 'background',
      priority: 'visible',
    });
  }
  try {
    const res = await fetch(input, init);
    const availabilitySignal = {
      status: res.status,
      message: res.statusText,
    };

    if (res.ok) {
      markBackendAvailable();
    } else if (isBackendUnavailableError(availabilitySignal)) {
      markBackendUnavailable(availabilitySignal);
    } else {
      markBackendAvailable();
    }

    return res;
  } catch (error) {
    markBackendUnavailable(error);
    throw error;
  }
}

function isWorkspaceScopedMarketplaceRequest(
  input: RequestInfo | URL
): boolean {
  const path =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  return /\/api\/workspaces?\//.test(path);
}

// =============================================================================
// Hook: useMarketplaceTemplates
// =============================================================================

export function useMarketplaceTemplates(
  filter: BrowseFilter,
  options?: {
    workspaceId?: string | null;
    includePublicCatalog?: boolean;
  }
) {
  const [templates, setTemplates] = useState<MarketplaceTemplate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchTemplates = useCallback(
    async (f: BrowseFilter) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const params = new URLSearchParams();
      if (f.search) params.set('search', f.search);
      if (f.category) params.set('category', f.category);
      if (f.page) params.set('page', String(f.page));
      if (f.limit) params.set('limit', String(f.limit));
      if (f.featured) params.set('featured', 'true');
      if (f.sortBy) params.set('sortBy', f.sortBy);
      if (f.templateType) params.set('templateType', f.templateType);
      if (f.templateFacet) params.set('facet', f.templateFacet);

      const workspaceId = options?.workspaceId ?? null;
      const includePublicCatalog =
        options?.includePublicCatalog === true && workspaceId !== null;
      const cacheScope = includePublicCatalog
        ? `available:${workspaceId}`
        : (workspaceId ?? 'global');
      const cacheKey = `templates:${cacheScope}:${params.toString()}`;
      const cached = getCached<BrowseResult>(cacheKey);

      if (cached) {
        setTemplates(cached.items);
        setTotal(cached.total);
        setPage(cached.page);
        setIsLoading(false);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const fetchCatalog = async (
          endpoint: string,
          requestWorkspaceId?: string | null
        ): Promise<BrowseResult> => {
          const res = await marketplaceFetch(
            endpoint,
            {
              credentials: 'include',
              signal: controller.signal,
            },
            requestWorkspaceId
          );
          if (!res.ok) {
            throw new Error(`Failed to fetch templates: ${res.status}`);
          }
          return res.json();
        };
        const query = params.toString();
        const data = includePublicCatalog
          ? await Promise.all([
              fetchCatalog(`/api/marketplace/templates?${query}`),
              fetchCatalog(
                `/api/workspace/${workspaceId}/marketplace/templates?${query}`,
                workspaceId
              ),
            ]).then(([publicResult, workspaceResult]) =>
              mergeMarketplaceBrowseResults(publicResult, workspaceResult)
            )
          : await fetchCatalog(
              workspaceId
                ? `/api/workspace/${workspaceId}/marketplace/templates?${query}`
                : `/api/marketplace/templates?${query}`,
              workspaceId
            );
        setCache(cacheKey, data);
        setTemplates(data.items);
        setTotal(data.total);
        setPage(data.page);
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          setError(err.message ?? 'Failed to fetch templates');
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [options?.includePublicCatalog, options?.workspaceId]
  );

  useEffect(() => {
    void fetchTemplates(filter);
    return () => {
      abortRef.current?.abort();
    };
  }, [filter, fetchTemplates]);

  return {
    templates,
    total,
    page,
    isLoading,
    error,
    refetch: () => fetchTemplates(filter),
  };
}

export function useMarketplaceBrowseTypeCounts(options?: {
  workspaceId?: string | null;
}) {
  const [counts, setCounts] = useState<MarketplaceBrowseTypeCounts | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const workspaceId = options?.workspaceId ?? null;

  useEffect(() => {
    const cacheKey = `marketplace:type-counts:${workspaceId ?? 'global'}`;
    const cached = getCached<MarketplaceBrowseTypeCounts>(cacheKey);
    if (cached) {
      setCounts(cached);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setCounts(null);
    setIsLoading(true);

    Promise.all(
      MARKETPLACE_BROWSE_COUNTED_TYPE_TABS.map(async tab => {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('limit', '1');
        params.set('sortBy', 'type');

        const typeFilter = deriveBrowseTypeFilter(tab);
        if (typeFilter.templateType) {
          params.set('templateType', typeFilter.templateType);
        }
        if (typeFilter.templateFacet) {
          params.set('facet', typeFilter.templateFacet);
        }

        const endpoint = workspaceId
          ? `/api/workspace/${workspaceId}/marketplace/templates?${params.toString()}`
          : `/api/marketplace/templates?${params.toString()}`;
        const res = await marketplaceFetch(
          endpoint,
          {
            credentials: 'include',
            signal: controller.signal,
          },
          workspaceId
        );
        if (!res.ok) {
          throw new Error(`Failed to fetch template count: ${res.status}`);
        }
        const data = (await res.json()) as BrowseResult;
        return [tab, data.total] as const;
      })
    )
      .then(entries => {
        if (controller.signal.aborted) return;
        const nextCounts = Object.fromEntries(
          entries
        ) as MarketplaceBrowseTypeCounts;
        setCache(cacheKey, nextCounts);
        setCounts(nextCounts);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCounts(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [workspaceId]);

  return { counts, isLoading };
}

// =============================================================================
// Hook: useInstalledTemplates (sl-mkx.1.3)
//
// Loads the set of installs in the current workspace so browse cards can
// render the correct state (install / installed / update-available) without
// re-issuing per-card queries. Returned map key is templateId.
// =============================================================================

export interface InstalledTemplateInfo {
  installId: string;
  templateId: string;
  templateTitle: string;
  templateType: string;
  stepInsertType: string | null;
  versionNumber: number;
  semanticVersion: string | null;
  latestVersion: number;
  latestSemanticVersion: string | null;
  installedDocIds: string[];
  workflowId: string | null;
  artifactSummary?: {
    primaryDocId: string | null;
    docCount: number;
    workflowId: string | null;
    artifactKinds: string[];
  };
  managementActions?: {
    primaryAction:
      | 'open_doc'
      | 'open_workflow'
      | 'use_step'
      | 'open_apps'
      | 'none';
    canOpenDoc: boolean;
    canOpenWorkflow: boolean;
    canUseStep: boolean;
    canRemove: boolean;
    removeMode: 'detach_install_record';
  };
  titlesI18n?: Record<string, string>;
  descriptionsI18n?: Record<string, string>;
  category?: string;
  tags?: string[];
  publishedByUserId?: string | null;
  publishedByWorkspaceId?: string | null;
  visibility?: 'GLOBAL' | 'WORKSPACE' | 'TENANT' | 'SYSTEM';
  previewImageBlobId?: string | null;
  averageRating?: number | null;
  publisher?: string;
  verified?: boolean;
  provenance?: 'streamlain_built' | 'streamlain_certified' | 'community_built';
  appBlockIds?: string[];
  installedAt: string;
  isLegacyInstall: boolean;
}

const INSTALLED_QUERY = /* GraphQL */ `
  query installedTemplates($workspaceId: String!) {
    installedTemplates(workspaceId: $workspaceId) {
      installId
      templateId
      templateTitle
      templateType
      stepInsertType
      versionNumber
      semanticVersion
      latestVersion
      latestSemanticVersion
      installedDocIds
      workflowId
      artifactSummary {
        primaryDocId
        docCount
        workflowId
        artifactKinds
      }
      managementActions {
        primaryAction
        canOpenDoc
        canOpenWorkflow
        canUseStep
        canRemove
        removeMode
      }
      installedAt
      isLegacyInstall
    }
  }
`;

export function indexInstalledTemplatesByTemplateId(
  installed: InstalledTemplateInfo[]
): Map<string, InstalledTemplateInfo> {
  const byTemplateId = new Map<string, InstalledTemplateInfo>();
  for (const i of installed) {
    if (!byTemplateId.has(i.templateId)) {
      byTemplateId.set(i.templateId, i);
    }
  }
  return byTemplateId;
}

export function useInstalledTemplates(workspaceId: string | null) {
  const [installed, setInstalled] = useState<InstalledTemplateInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!workspaceId) {
      setInstalled([]);
      return;
    }
    setIsLoading(true);
    try {
      const res = await marketplaceFetch(
        '/graphql',
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: INSTALLED_QUERY,
            variables: { workspaceId },
          }),
        },
        workspaceId
      );
      if (!res.ok) {
        setInstalled([]);
        return;
      }
      const json = await res.json();
      const list = json?.data?.installedTemplates ?? [];
      setInstalled(list as InstalledTemplateInfo[]);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);
  useMarketplaceInstallStateRefresh(workspaceId, refetch);

  const byTemplateId = indexInstalledTemplatesByTemplateId(installed);

  return { installed, byTemplateId, isLoading, refetch };
}

export function useMarketplaceTemplateDetail(
  workspaceId: string | null | undefined,
  templateId: string | null
) {
  const [template, setTemplate] = useState<MarketplaceTemplate | null>(null);

  useEffect(() => {
    if (!templateId) {
      setTemplate(null);
      return;
    }
    const controller = new AbortController();
    const load = async () => {
      const workspaceResponse = workspaceId
        ? await workspaceApiFetch(
            `/api/workspace/${workspaceId}/marketplace/templates/${templateId}`,
            { credentials: 'include', signal: controller.signal }
          )
        : null;
      const response =
        workspaceResponse?.ok === true
          ? workspaceResponse
          : await fetch(`/api/marketplace/templates/${templateId}`, {
              credentials: 'include',
              signal: controller.signal,
            });
      if (!response.ok) throw new Error('Marketplace template not found');
      setTemplate((await response.json()) as MarketplaceTemplate);
    };
    void load().catch(error => {
      if (!controller.signal.aborted && !isBackendUnavailableError(error)) {
        setTemplate(null);
      }
    });
    return () => controller.abort();
  }, [templateId, workspaceId]);

  return template;
}

const ROUTE_APP_BLOCK_MARKETPLACE_FILTER: BrowseFilter = {
  templateType: 'app_block',
  page: 1,
  limit: 100,
  sortBy: 'type',
};

/**
 * App Blocks are repeatable Marketplace blueprints, not workspace-level
 * installs. The Builder therefore browses the catalog directly and sends the
 * selected version to the target-bound instantiation endpoint.
 */
export function useRouteAppBlockMarketplaceTemplates(
  workspaceId: string | null,
  search = ''
) {
  const filter = useMemo<BrowseFilter>(
    () => ({
      ...ROUTE_APP_BLOCK_MARKETPLACE_FILTER,
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [search]
  );
  return useMarketplaceTemplates(filter, {
    workspaceId,
    includePublicCatalog: true,
  });
}

// =============================================================================
// Hook: useMarketplaceVersionRange (sl-mkx.2.2/2.3)
//
// Loads the changelog entries between the installed version (exclusive) and
// the target latest version (inclusive). Re-fetches when any input changes.
// =============================================================================

export interface MarketplaceVersionInfo {
  versionNumber: number;
  semanticVersion: string;
  changelogI18n?: Record<string, string>;
  changelog: string;
  createdAt: string;
  credentialRequirements: Array<{
    credentialId?: string;
    required?: boolean;
    [key: string]: unknown;
  }>;
  hasNewRequiredCredentials: boolean;
  /**
   * sl-mkx.6-rework-3 (F-UX-4): subset of this version's required credential
   * slots that are NOT present in the installed baseline. Drives the update
   * modal's rebind dropdown. `{type, label, required}` per entry.
   */
  newRequiredCredentials: Array<{
    type: string;
    label: string;
    required: true;
  }>;
  hasNewRequiredConfigValues: boolean;
  newRequiredConfigValues: Array<{
    key: string;
    label?: string;
    description?: string;
    required: true;
    defaultValue?: string;
  }>;
  breakingHints: MarketplaceVersionBreakingHint[];
}

export type MarketplaceVersionBreakingHint =
  | {
      code: 'new_required_credentials';
      severity: 'blocking';
      versionNumber: number;
      credentialTypes?: string[];
    }
  | {
      code: 'new_required_config_values';
      severity: 'blocking';
      versionNumber: number;
      configKeys?: string[];
    }
  | {
      code: 'semver_major';
      severity: 'warning';
      versionNumber: number;
      semanticVersion?: string;
      fromMajor?: number;
      toMajor?: number;
    }
  | {
      code: string;
      severity: 'blocking' | 'warning';
      versionNumber?: number;
      [key: string]: unknown;
    };

const VERSION_RANGE_QUERY = /* GraphQL */ `
  query marketplaceVersionRange(
    $workspaceId: String!
    $templateId: ID!
    $fromVersion: Int!
    $toVersion: Int!
  ) {
    marketplaceVersionRange(
      workspaceId: $workspaceId
      templateId: $templateId
      fromVersion: $fromVersion
      toVersion: $toVersion
    ) {
      versionNumber
      semanticVersion
      changelog
      createdAt
      credentialRequirements
      hasNewRequiredCredentials
      newRequiredCredentials
      hasNewRequiredConfigValues
      newRequiredConfigValues
      breakingHints
    }
  }
`;

export function useMarketplaceVersionRange(args: {
  workspaceId: string | null;
  templateId: string | null;
  fromVersion: number | null;
  toVersion: number | null;
}) {
  const { workspaceId, templateId, fromVersion, toVersion } = args;
  const [versions, setVersions] = useState<MarketplaceVersionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (
      !workspaceId ||
      !templateId ||
      fromVersion == null ||
      toVersion == null ||
      toVersion <= fromVersion
    ) {
      setVersions([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    marketplaceFetch(
      '/graphql',
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: VERSION_RANGE_QUERY,
          variables: { workspaceId, templateId, fromVersion, toVersion },
        }),
      },
      workspaceId
    )
      .then(res => (res.ok ? res.json() : Promise.reject(res.statusText)))
      .then(json => {
        if (cancelled) return;
        const errs = json?.errors;
        if (errs && errs.length > 0) {
          setError(errs[0]?.message ?? 'Failed to load changelog');
          setVersions([]);
          return;
        }
        setVersions(
          (json?.data?.marketplaceVersionRange ??
            []) as MarketplaceVersionInfo[]
        );
      })
      .catch(e => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId, templateId, fromVersion, toVersion]);

  const targetVersionInfo = versions[versions.length - 1] ?? null;
  const hasNewRequiredCredentials = Boolean(
    targetVersionInfo?.hasNewRequiredCredentials
  );
  const hasNewRequiredConfigValues = Boolean(
    targetVersionInfo?.hasNewRequiredConfigValues
  );

  // Intermediate changelog rows are informational. Blocking prerequisites
  // must come from the selected target version only; otherwise v2 could add a
  // required slot, v3 remove it, and v1->v3 would still be blocked.
  const newRequiredCredentials = (() => {
    const seen = new Map<string, { type: string; label: string }>();
    const list = targetVersionInfo?.newRequiredCredentials ?? [];
    for (const slot of list) {
      if (!seen.has(slot.type)) {
        seen.set(slot.type, { type: slot.type, label: slot.label });
      }
    }
    return Array.from(seen.values()).map(s => ({
      type: s.type,
      label: s.label,
      required: true as const,
    }));
  })();

  const newRequiredConfigValues = (() => {
    const seen = new Map<
      string,
      {
        key: string;
        label?: string;
        description?: string;
        defaultValue?: string;
      }
    >();
    const list = targetVersionInfo?.newRequiredConfigValues ?? [];
    for (const config of list) {
      if (!seen.has(config.key)) {
        seen.set(config.key, {
          key: config.key,
          label: config.label,
          description: config.description,
          defaultValue: config.defaultValue,
        });
      }
    }
    return Array.from(seen.values()).map(config => ({
      ...config,
      required: true as const,
    }));
  })();

  return {
    versions,
    isLoading,
    error,
    hasNewRequiredCredentials,
    newRequiredCredentials,
    hasNewRequiredConfigValues,
    newRequiredConfigValues,
  };
}

// =============================================================================
// Hook: useMarketplaceCategories
// =============================================================================

export function useMarketplaceCategories(options?: {
  workspaceId?: string | null;
}) {
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const workspaceId = options?.workspaceId ?? null;

  useEffect(() => {
    const cacheKey = `marketplace:categories:${workspaceId ?? 'global'}`;
    const cached = getCached<MarketplaceCategory[]>(cacheKey);
    if (cached) {
      setCategories(cached);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const endpoint = workspaceId
      ? `/api/workspace/${workspaceId}/marketplace/categories`
      : '/api/marketplace/categories';

    marketplaceFetch(endpoint, { credentials: 'include' }, workspaceId)
      .then(res => (res.ok ? res.json() : []))
      .then((data: MarketplaceCategory[]) => {
        if (!cancelled) {
          setCache(cacheKey, data);
          setCategories(data);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return { categories, isLoading };
}
